import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * Reusable confirmation modal for high-stakes admin actions (approve, send-now,
 * cancel scheduled, retry, etc.). Requires the user to type a confirmation
 * phrase before the destructive button becomes clickable.
 */
export default function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  confirmPhrase,
  tone = "primary", // primary | danger | warning
  onConfirm,
  testId = "confirm-action",
  extra,
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const okToConfirm = !confirmPhrase || typed.trim() === confirmPhrase;

  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
      setTyped("");
    } finally {
      setBusy(false);
    }
  };

  const bg = tone === "danger" ? "#B04635" : tone === "warning" ? "var(--ta-secondary)" : "var(--ta-primary)";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-[24px]" data-testid={`${testId}-modal`}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            {tone !== "primary" && (
              <div
                className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0"
                style={{ background: "#FFF3D6", color: "#8A5A00" }}
              >
                <AlertTriangle size={20} />
              </div>
            )}
            <div>
              <DialogTitle className="font-display text-xl font-semibold" style={{ color: "var(--ta-primary)" }}>
                {title}
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
                {description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {extra && <div className="mt-2">{extra}</div>}

        {confirmPhrase && (
          <div className="mt-2">
            <label className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
              Type <span className="font-mono font-semibold" style={{ color: "var(--ta-primary)" }}>{confirmPhrase}</span> to confirm
            </label>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              data-testid={`${testId}-phrase-input`}
              className="mt-2 w-full rounded-xl bg-white border-2 px-4 py-3 text-base outline-none focus:border-[var(--ta-primary)]"
              style={{ borderColor: "var(--ta-border)", color: "var(--ta-text)" }}
              autoFocus
            />
          </div>
        )}

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => onOpenChange(false)}
            data-testid={`${testId}-cancel`}
            className="flex-1 min-h-[48px] rounded-xl text-sm font-medium border-2"
            style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
          >
            Never mind
          </button>
          <button
            onClick={submit}
            disabled={busy || !okToConfirm}
            data-testid={`${testId}-submit`}
            className="flex-1 min-h-[48px] rounded-xl text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: bg }}
          >
            {busy ? <Loader2 size={18} className="animate-spin inline" /> : confirmLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
